import { axiosInstance } from './axiosInstance';
import { token_keys } from '../constants';

// Read one Server-Sent Events response, dispatching frames to callbacks.
//
// Factored out of streamRiskInsight, whose framing the impact AI endpoint also
// speaks: `event:` / `data:` lines, frames delimited by a blank line, a JSON
// `data` payload per frame, and meta -> token* -> done, with error only on a
// mid-stream failure.
//
// fetch rather than EventSource because EventSource cannot set an
// Authorization header.
//
// Any other named event (the By-group chat's `redirect` / `replace`) goes to
// `onEvent(event, data)`, so an endpoint can extend the contract without a
// new reader.
//
// Returns a function that aborts the stream. Errors BEFORE the stream opens
// (validation, 403, 404) arrive as ordinary 4xx JSON, so those are read off the
// body and reported through onError rather than being lost behind a bare status
// code.
const streamSse = (url, body, { onMeta, onToken, onDone, onError, onEvent } = {}) => {
    const controller = new AbortController();

    (async () => {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                    'Content-Type': 'application/json',
                    Accept: 'text/event-stream',
                },
                body: JSON.stringify(body),
                signal: controller.signal,
            });

            if (!response.ok || !response.body) {
                // A rejected request never becomes a stream: the body here is the
                // endpoint's own JSON error, which says something far more useful
                // than the status alone.
                let detail = null;
                try {
                    detail = await response.json();
                } catch {
                    detail = null;
                }
                onError &&
                    onError({
                        status: response.status,
                        error: pickErrorMessage(detail) || `Request failed with status ${response.status}`,
                        detail,
                    });
                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            // Parse one complete SSE frame (already split on the blank line).
            // A frame is a set of "event:"/"data:" lines; data lines are joined.
            const handleFrame = (frame) => {
                let event = 'message';
                const dataLines = [];
                frame.split('\n').forEach((line) => {
                    if (line.startsWith('event:')) {
                        event = line.slice(6).trim();
                    } else if (line.startsWith('data:')) {
                        dataLines.push(line.slice(5).replace(/^ /, ''));
                    }
                });
                const data = dataLines.join('\n');

                if (event === 'done') {
                    onDone && onDone();
                    return;
                }

                let parsed = null;
                if (data) {
                    try {
                        parsed = JSON.parse(data);
                    } catch {
                        parsed = null;
                    }
                }

                if (event === 'meta') {
                    onMeta && onMeta(parsed || {});
                } else if (event === 'token') {
                    const t = parsed && typeof parsed.t === 'string' ? parsed.t : '';
                    if (t) onToken && onToken(t);
                } else if (event === 'error') {
                    onError && onError(parsed || { error: 'stream error' });
                } else {
                    onEvent && onEvent(event, parsed || {});
                }
            };

            // eslint-disable-next-line no-constant-condition
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                let sep;
                // SSE frames are delimited by a blank line ("\n\n").
                while ((sep = buffer.indexOf('\n\n')) !== -1) {
                    const frame = buffer.slice(0, sep);
                    buffer = buffer.slice(sep + 2);
                    if (frame.trim()) handleFrame(frame);
                }
            }

            // Flush any trailing frame that wasn't newline-terminated.
            if (buffer.trim()) handleFrame(buffer);
        } catch (error) {
            // An abort is this caller's own doing, not a failure to report.
            if (error.name === 'AbortError') return;
            console.error('SSE stream failed:', url, error);
            onError && onError({ error: error.message || 'stream error' });
        }
    })();

    return () => controller.abort();
};

// Best-effort human-readable message out of a DRF error body, which is either
// { error: "..." } or { <field>: ["...", ...] }.
const pickErrorMessage = (detail) => {
    if (!detail || typeof detail !== 'object') return null;
    if (typeof detail.error === 'string') return detail.error;
    if (typeof detail.detail === 'string') return detail.detail;
    const first = Object.values(detail).find((v) => Array.isArray(v) && typeof v[0] === 'string');
    return first ? first[0] : null;
};

// Insights API service
export const insightsApi = {
    // Get list of all insights/reports
    getInsightsList: async (params = {}) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const queryParams = new URLSearchParams();
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);

            const url = `/insights/reports/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
            const response = await axiosInstance.get(url, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching insights list:', error);
            throw error;
        }
    },

    getInsightsList2: async (params = {}) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const queryParams = new URLSearchParams();
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);

            const url = `/insights/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
            const response = await axiosInstance.get(url, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching insights list:', error);
            throw error;
        }
    },

    // Get detailed insight report data for a specific insight
    getInsightReport: async (insightId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(`/insights/reports/${insightId}/`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching insight report:', error);
            throw error;
        }
    },

    // Run the WHOLE insight pipeline for one insight as a single background job:
    // raw_input -> pillars -> topics -> report (+ vectorization) ->
    // signals (the Risk tab's data). This is the one-job BE path; the older
    // config-only autoconfig flow is gone.
    //
    // POST /insights/<insight_id>/build/
    //   -> 202 { job_id, execution_id, insight_id, name, from_stage, skip_signals }
    //
    // Progress is polled from the SAME getInsightStatus() endpoint as the other
    // generate flows, so the loading card needs nothing new. Options:
    //   name         name for the Config/Report this run produces
    //   fromStage    'raw_input'|'pillars'|'topics'|'report'|'signals' — resume a
    //                failed run without re-paying for topic modelling
    //   skipSignals  stop after the report (no Risk tab, ~half the LLM spend)
    //   signalLimit  score only the first N respondents (cheap smoke test)
    buildInsight: async (insightId, { name = null, fromStage = null, skipSignals = false, signalLimit = null } = {}) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const queryParams = new URLSearchParams();
            if (name) queryParams.append('name', name);
            if (fromStage) queryParams.append('from_stage', fromStage);
            if (skipSignals) queryParams.append('skip_signals', 'true');
            if (signalLimit) queryParams.append('signal_limit', String(signalLimit));

            const query = queryParams.toString();
            const url = `/insights/${insightId}/build/${query ? '?' + query : ''}`;
            const response = await axiosInstance.post(url, {}, { headers });
            return response.data;
        } catch (error) {
            console.error('Error triggering insight pipeline for insight:', insightId, error);
            throw error;
        }
    },

    // Declare the pillar taxonomy for an existing insight, before its pipeline
    // runs. Stored as raw_input['pillars_def'], which autoconfig suggests
    // against and which reaches the report JSON.
    //
    // PUT /insights/<insight_id>/pillars/  { pillars_def: {name: definition} }
    // An empty map clears the taxonomy and restores the default behaviour.
    saveInsightPillars: async (insightId, pillarsDef) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.put(
                `/insights/${insightId}/pillars/`,
                { pillars_def: pillarsDef },
                { headers },
            );
            return response.data;
        } catch (error) {
            console.error('Error saving pillar definitions for insight:', insightId, error);
            throw error;
        }
    },

    // Approve the pillar tagging and let the rest of the run proceed.
    //
    // POST /insights/<insight_id>/pillars/confirm/
    //   { open_text_pillars: {question: pillar}, likert_pillars: {...} }
    //   -> 202 { insight_id, config_id, open_text_pillars, likert_pillars, execution_id }
    //   -> 409 { code: 'not_awaiting_confirmation' } when the run was already
    //      confirmed — one confirmation per parked run, never a second job
    //
    // The run is parked at STEP_AWAITING_CONFIRMATION with a Config holding the
    // suggested tagging and no topics. Both maps are optional: omit them to
    // accept the suggestions as they are. Resumes at the `topics` stage, which
    // is where the real spend starts.
    confirmPillarTagging: async (insightId, { openTextPillars = null, likertPillars = null } = {}) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const body = {};
            if (openTextPillars) body.open_text_pillars = openTextPillars;
            if (likertPillars) body.likert_pillars = likertPillars;
            const response = await axiosInstance.post(
                `/insights/${insightId}/pillars/confirm/`, body, { headers },
            );
            return response.data;
        } catch (error) {
            console.error('Error confirming pillar tagging for insight:', insightId, error);
            throw error;
        }
    },

    // Import a raw_input JSON document and run the full pipeline on it.
    // POST /insights/upload-json/  (multipart)
    //
    // Two calls, one endpoint:
    //   checkOnly: true  -> 200 { valid, title, summary, missing, will_infer }
    //                       validates only; creates nothing, spends nothing.
    //   checkOnly: false -> 202 { insight_id, survey_id, name, job_id,
    //                             execution_id, summary, notes }
    //                       normalizes the document, creates the insight (with a
    //                       placeholder survey) and starts config -> report ->
    //                       signals. Poll getInsightStatus(insight_id).
    //
    // A malformed document is 400 with `data.error` either way. An older export
    // missing likert_groups / likert_pillars is NOT malformed: those are filled
    // in, and `will_infer` / `notes` say how.
    uploadRawInput: async (file, { title = null, checkOnly = false, language = 'en', pillarsDef = null } = {}) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'multipart/form-data',
            };
            const formData = new FormData();
            formData.append('file', file);
            if (title) formData.append('title', title);
            if (checkOnly) formData.append('check_only', 'true');
            if (language) formData.append('language', language);
            // Sent as a JSON string: this is a multipart request, and the
            // endpoint creates the insight AND starts the pipeline in one call,
            // so there is no later moment at which to declare the taxonomy.
            if (pillarsDef && Object.keys(pillarsDef).length) {
                formData.append('pillars_def', JSON.stringify(pillarsDef));
            }
            const response = await axiosInstance.post('/insights/upload-json/', formData, { headers });
            return response.data;
        } catch (error) {
            console.error('Error uploading raw_input JSON:', error);
            throw error;
        }
    },

    // Delete an insight and everything derived from it.
    // DELETE /insights/<insight_id>/
    //   -> 200 { insight_id, survey_id, survey_deleted, total,
    //            deleted: {"insights.Report": n, "insights.RiskPredictionItem": n, ...},
    //            counts, projects_without_waves, vectors_deleted }
    //
    // Hard delete, not the old soft delete: reports, configs, statuses, risk
    // rows, project waves, shares and RAG vectors all go.
    // The SURVEY it was computed from is kept (questions, collectors, answers) —
    // except a JSON-import placeholder survey, which goes with it. Stakeholder
    // groups, impacts and leaders survive with their `insight` set to null.
    deleteInsight: async (insightId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.delete(`/insights/${insightId}/`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error deleting insight:', insightId, error);
            throw error;
        }
    },

    // Poll report/config generation status for an insight.
    // GET /insights/<insight_id>/status/
    //   -> { state: 'running'|'awaiting_confirmation'|'finished'|'errored',
    //        step: 1-7, step_labels: [...], error?: str }
    // 'finished' only once the risk signals are scored too, not when the
    // Report row is first saved.
    getInsightStatus: async (insightId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(`/insights/${insightId}/status/`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching insight status:', error);
            throw error;
        }
    },

    // Get chat history sessions for a report
    getChatHistorySessions: async (reportId, channel) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(`/insights/chat-history/?report-id=${reportId}&channel=${channel}`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching chat history sessions:', error);
            throw error;
        }
    },

    // Get chat messages for a specific session
    getChatSessionMessages: async (sessionId, channel) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(`/insights/chat-history/${sessionId}?channel=${channel}`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching chat session messages:', error);
            throw error;
        }
    },

     // Get project chat history sessions for a report
getProjectChatHistorySessions: async (projectId, channel) => {
    try {
        const headers = {
            Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
        };

        const response = await axiosInstance.get(
            `/insights/project-history/?project-id=${projectId}&channel=${channel}`,
            { headers }
        );

        return response.data;
    } catch (error) {
        console.error('Error fetching project chat history sessions:', error);
        throw error;
    }
},

        // Get chat messages for a specific session
getProjectChatSessionMessages: async (sessionId, channel) => {
    try {
        const headers = {
            Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
        };

        const response = await axiosInstance.get(
            `/insights/project-history/${sessionId}?channel=${channel}`,
            { headers }
        );

        return response.data;
    } catch (error) {
        console.error('Error fetching project chat session messages:', error);
        throw error;
    }
},

    // Get all insight configs for report generation
    getInsightConfigs: async (params = {}) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const queryParams = new URLSearchParams();
            queryParams.append('all', 'true');
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);
            if (params.search) queryParams.append('search', params.search);

            const url = `/insights/configs/?${queryParams.toString()}`;
            const response = await axiosInstance.get(url, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching insight configs:', error);
            throw error;
        }
    },

    // Rename an insight config
    renameConfig: async (configId, name) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.patch(`/insights/configs/${configId}/name/`, { name }, { headers });
            return response.data;
        } catch (error) {
            console.error('Error renaming config:', error);
            throw error;
        }
    },

    // Rename an insight report
    renameReport: async (reportId, name) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.patch(`/insights/reports/${reportId}/name/`, { name }, { headers });
            return response.data;
        } catch (error) {
            console.error('Error renaming report:', error);
            throw error;
        }
    },

    // Delete a report and everything scoped to it.
    // DELETE /insights/reports/<report_id>/
    //   -> 200 { report_id, name, insight_id,
    //            deleted: { waves: [{id, project_id, wave_count}], shares, vectors },
    //            reports_left_on_insight, projects_without_waves: [id] }
    //
    // The report's waves go with it (a wave IS a report inside a project), as do
    // its shares and its RAG vectors. The projects themselves are kept — they
    // own documents and chat history of their own — so `projects_without_waves`
    // names any project this emptied. Owner of the survey, or staff, only: a
    // user the report was merely shared with gets 403.
    deleteReport: async (reportId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.delete(`/insights/reports/${reportId}/`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error deleting report:', reportId, error);
            throw error;
        }
    },

    // Edit a single topic's title inside a report's results JSON.
    // Body: { question, topic_id (int|null), topic_index (int|null), title }
    editTopicTitle: async (reportId, { question, topic_id, topic_index, title }) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.patch(
                `/insights/reports/${reportId}/topic-title/`,
                { question, topic_id, topic_index, title },
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error updating topic title:', error);
            throw error;
        }
    },

    // ── Per-question topic recompute ──
    //
    // Two steps on purpose: `create` only PROPOSES new topics for one open-text
    // question (a job builds it; the report is untouched), and `confirm` is the
    // step that writes them into the report, the config and the vector index.

    // Propose topics for a question. Pass EITHER `titles` (the exact list —
    // responses are only classified into those names) OR `num_topics` (cluster
    // into that many and let the LLM name them). Returns the proposal row;
    // poll getTopicRecompute until its status leaves pending/running.
    createTopicRecompute: async (reportId, { question, titles, num_topics }) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const body = titles ? { question, titles } : { question, num_topics };
            const response = await axiosInstance.post(
                `/insights/reports/${reportId}/topics/recompute/`,
                body,
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error starting topic recompute:', error);
            throw error;
        }
    },

    // Poll one proposal: status (pending|running|ready|applied|failed|discarded),
    // `preview.topics` once ready, `error` when the build was rejected.
    getTopicRecompute: async (reportId, recomputeId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(
                `/insights/reports/${reportId}/topics/recompute/${recomputeId}/`,
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching topic recompute:', error);
            throw error;
        }
    },

    // Apply a ready proposal to the report. Returns the confirmed topics.
    confirmTopicRecompute: async (reportId, recomputeId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.post(
                `/insights/reports/${reportId}/topics/recompute/${recomputeId}/confirm/`,
                {},
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error confirming topic recompute:', error);
            throw error;
        }
    },

    // Throw away an unconfirmed proposal.
    discardTopicRecompute: async (reportId, recomputeId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.delete(
                `/insights/reports/${reportId}/topics/recompute/${recomputeId}/`,
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error discarding topic recompute:', error);
            throw error;
        }
    },

    // Rename an insight
    renameInsight: async (insightId, name) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.patch(`/insights/${insightId}/name/`, { name }, { headers });
            return response.data;
        } catch (error) {
            console.error('Error renaming insight:', error);
            throw error;
        }
    },

    // ── Ask Prompta source selector ──

    // Which RAG sources this project actually has: its waves, its documents,
    // and whether it has a risk model / impact register at all. The selector
    // renders only what comes back, so a project with no documents never shows
    // a Documents group.
    //
    // Sources are project-scoped, so `project_id` is the direct form — used by
    // the project-wide comparison chat, which knows its project. The
    // `?report_id=` alias serves the report-scoped chat, which knows only its
    // report; the backend resolves it to the project exactly as `getDocuments`
    // above does, so both callers get the same payload.
    getRagSources: async (reportId = null, { project_id } = {}) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const url =
                project_id != null && project_id !== ''
                    ? `/insights/projects/${project_id}/rag-sources/`
                    : `/insights/rag-sources/?report_id=${reportId}`;

            const response = await axiosInstance.get(url, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching RAG sources:', error);
            throw error;
        }
    },

    // ── Documents ──

    // List documents. Supports pagination: { page, page_size }.
    // `reportId` maps to ?report_id= — the backend resolves it to that report's
    // project, so the returned set is every document in that project.
    // `project_id` names the project directly, for callers that already know it
    // (the project workspace's Documents tab) and have no report to go through.
    getDocuments: async (reportId = null, { page = 1, page_size, project_id } = {}) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };

            const params = new URLSearchParams();
            if (reportId) params.set('report_id', reportId);
            if (project_id != null && project_id !== '') params.set('project_id', project_id);
            if (page != null) params.set('page', page);
            if (page_size != null) params.set('page_size', page_size);
            const query = params.toString();
            const url = query ? `/documents/?${query}` : '/documents/';

            const response = await axiosInstance.get(url, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching documents:', error);
            throw error;
        }
    },

    // Get a single document by id
    getDocument: async (documentId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(`/documents/${documentId}/`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching document:', error);
            throw error;
        }
    },

    // Upload a document (multipart/form-data). A document is scoped to a project:
    // the backend requires `project_id` at upload and there is no post-upload
    // linking step, so this is the only moment the document's scope is set.
    uploadDocument: async (file, projectId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'multipart/form-data',
            };
            const formData = new FormData();
            formData.append('file', file);
            if (projectId != null && projectId !== '') formData.append('project_id', projectId);
            const response = await axiosInstance.post('/documents/upload/', formData, { headers });
            return response.data;
        } catch (error) {
            console.error('Error uploading document:', error);
            throw error;
        }
    },

    // Download a document's file. The backend streams the bytes (converted PDF
    // when available, otherwise the original file) through an authenticated
    // endpoint — the raw GCS urls are private, so we must send the auth header
    // and read the response as a blob. Returns the full axios response so the
    // caller can read Content-Disposition for the filename.
    downloadDocument: async (documentId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(`/documents/${documentId}/download/`, {
                headers,
                responseType: 'blob',
            });
            return response;
        } catch (error) {
            console.error('Error downloading document:', error);
            throw error;
        }
    },

    // Delete a document (DELETE /documents/<document_id>/) — 204 No Content on success
    deleteDocument: async (documentId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            await axiosInstance.delete(`/documents/${documentId}/`, { headers });
        } catch (error) {
            console.error('Error deleting document:', error);
            throw error;
        }
    },

    // ── Projects & Waves ──

    // Paginated project list. Each result is the project plus what it holds:
    //   { id, name, created_at,
    //     waves: [{id, wave_count, report_id, report_name}],
    //     counts: {waves, documents, impacts, stakeholder_groups},   // exact
    //     documents: [{id, name, created_at}],                       // preview
    //     impacts: [{id, what_is_changing, level, group_name}] }     // preview
    // The counts are the whole truth; `documents` and `impacts` are capped
    // server-side (PROJECT_CARD_PREVIEW_LIMIT), so a card's list can be shorter
    // than its count says — the project workspace is where the full set lives.
    getProjects: async (params = {}) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const queryParams = new URLSearchParams();
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);
            if (params.with_areas) queryParams.append('with_areas', '1');
            const url = `/insights/projects/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
            const response = await axiosInstance.get(url, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching projects:', error);
            throw error;
        }
    },

    createProject: async (name) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.post('/insights/projects/', { name }, { headers });
            return response.data;
        } catch (error) {
            console.error('Error creating project:', error);
            throw error;
        }
    },

    // One project's full comparison payload:
    //   { project_id, reports[], pillars[], keywords[], risk_filters{} }
    // Addressed by project rather than by an ordered list of report ids,
    // so the FE no longer has to carry an ordered list of report ids in the URL
    // to render a project's waves. `reports[]` is ordered by `order`.
    getProject: async (projectId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(`/insights/projects/${projectId}/`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching project:', error);
            throw error;
        }
    },

    getRiskPrediction: async (reportIds, filters = {}) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const body = { report_ids: reportIds };
            if (Object.keys(filters).length > 0) body.filters = filters;
            const response = await axiosInstance.post('/insights/risk-prediction/', body, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching risk prediction:', error);
            throw error;
        }
    },

    // The report's "By group" tab: the pillar signal bars re-scored for the
    // whole wave (baseline), every area and every registered stakeholder group.
    // GET /insights/reports/<report_id>/group-scores/
    //   -> { report_id, wave_total, pillars, baseline, areas, groups,
    //        ungrouped, hidden }
    // Scores are signed percentages already clamped to ±100 by the server.
    getGroupScores: async (reportId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(`/insights/reports/${reportId}/group-scores/`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching group scores:', error);
            throw error;
        }
    },

    // Generate a natural-language AI explanation for a single report's risk
    // radar, scoped to the same filter slice the radar is showing.
    // POST /insights/risk-insight/ { report_id, filters }
    //   -> { explanation, scores, drivers, slice, n_quotes }
    getRiskInsight: async (reportId, filters = {}) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.post(
                '/insights/risk-insight/',
                { report_id: reportId, filters },
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching risk insight:', error);
            throw error;
        }
    },

    // Stream a natural-language AI explanation for a single report's risk
    // radar, token-by-token, over Server-Sent Events.
    //
    // We use fetch (not EventSource) so the Authorization: Bearer header can be
    // sent — EventSource can't set headers. POST /insights/risk-insight/stream/
    // with { report_id, filters }. The response is an SSE stream of frames:
    //   event: meta\ndata: { scores, drivers, slice, n_quotes }
    //   event: token\ndata: { "t": "<chunk>" }   (many)
    //   event: done\ndata: {}
    //   event: error\ndata: { "error": "..." }
    //
    // Callbacks: onMeta(obj) / onToken(text) / onDone() / onError(obj).
    // Returns a function that aborts the stream when called.
    streamRiskInsight: (reportId, filters = {}, { onMeta, onToken, onDone, onError } = {}) => {
        const controller = new AbortController();

        (async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/v1/insights/risk-insight/stream/`,
                    {
                        method: 'POST',
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                            'Content-Type': 'application/json',
                            Accept: 'text/event-stream',
                        },
                        body: JSON.stringify({ report_id: reportId, filters }),
                        signal: controller.signal,
                    }
                );

                if (!response.ok || !response.body) {
                    onError && onError({ error: `Request failed with status ${response.status}` });
                    return;
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                // Parse one complete SSE frame (already split on the blank line).
                // A frame is a set of "event:"/"data:" lines; data lines are joined.
                const handleFrame = (frame) => {
                    let event = 'message';
                    const dataLines = [];
                    frame.split('\n').forEach((line) => {
                        if (line.startsWith('event:')) {
                            event = line.slice(6).trim();
                        } else if (line.startsWith('data:')) {
                            dataLines.push(line.slice(5).replace(/^ /, ''));
                        }
                    });
                    const data = dataLines.join('\n');

                    if (event === 'done') {
                        onDone && onDone();
                        return;
                    }

                    let parsed = null;
                    if (data) {
                        try {
                            parsed = JSON.parse(data);
                        } catch {
                            parsed = null;
                        }
                    }

                    if (event === 'meta') {
                        onMeta && onMeta(parsed || {});
                    } else if (event === 'token') {
                        const t = parsed && typeof parsed.t === 'string' ? parsed.t : '';
                        if (t) onToken && onToken(t);
                    } else if (event === 'error') {
                        onError && onError(parsed || { error: 'stream error' });
                    }
                };

                // eslint-disable-next-line no-constant-condition
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });

                    let sep;
                    // SSE frames are delimited by a blank line ("\n\n").
                    while ((sep = buffer.indexOf('\n\n')) !== -1) {
                        const frame = buffer.slice(0, sep);
                        buffer = buffer.slice(sep + 2);
                        if (frame.trim()) handleFrame(frame);
                    }
                }

                // Flush any trailing frame that wasn't newline-terminated.
                if (buffer.trim()) handleFrame(buffer);
            } catch (error) {
                if (error.name === 'AbortError') return;
                console.error('Error streaming risk insight:', error);
                onError && onError({ error: error.message || 'stream error' });
            }
        })();

        return () => controller.abort();
    },

    // Stream a natural-language AI explanation that COMPARES two reports' risk
    // radars (wave 1 vs wave 2), token-by-token, over Server-Sent Events.
    //
    // Mirrors streamRiskInsight: fetch (so the Authorization: Bearer header can
    // be sent — EventSource can't), an AbortController, and the same SSE frame
    // parser. POST /insights/risk-insight/compare/stream/ with
    // { report_id_1, report_id_2, filters }. The response is an SSE stream:
    //   event: meta\ndata: { wave1, wave2 }
    //   event: token\ndata: { "t": "<chunk>" }   (many)
    //   event: done\ndata: {}
    //   event: error\ndata: { "error": "..." }
    //
    // Callbacks: onMeta({ wave1, wave2 }) / onToken(text) / onDone() / onError(obj).
    // Returns a function that aborts the stream when called.
    streamRiskInsightCompare: (reportId1, reportId2, filters = {}, { onMeta, onToken, onDone, onError } = {}) => {
        const controller = new AbortController();

        (async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/v1/insights/risk-insight/compare/stream/`,
                    {
                        method: 'POST',
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                            'Content-Type': 'application/json',
                            Accept: 'text/event-stream',
                        },
                        body: JSON.stringify({ report_id_1: reportId1, report_id_2: reportId2, filters }),
                        signal: controller.signal,
                    }
                );

                if (!response.ok || !response.body) {
                    onError && onError({ error: `Request failed with status ${response.status}` });
                    return;
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                // Parse one complete SSE frame (already split on the blank line).
                // A frame is a set of "event:"/"data:" lines; data lines are joined.
                const handleFrame = (frame) => {
                    let event = 'message';
                    const dataLines = [];
                    frame.split('\n').forEach((line) => {
                        if (line.startsWith('event:')) {
                            event = line.slice(6).trim();
                        } else if (line.startsWith('data:')) {
                            dataLines.push(line.slice(5).replace(/^ /, ''));
                        }
                    });
                    const data = dataLines.join('\n');

                    if (event === 'done') {
                        onDone && onDone();
                        return;
                    }

                    let parsed = null;
                    if (data) {
                        try {
                            parsed = JSON.parse(data);
                        } catch {
                            parsed = null;
                        }
                    }

                    if (event === 'meta') {
                        onMeta && onMeta(parsed || {});
                    } else if (event === 'token') {
                        const t = parsed && typeof parsed.t === 'string' ? parsed.t : '';
                        if (t) onToken && onToken(t);
                    } else if (event === 'error') {
                        onError && onError(parsed || { error: 'stream error' });
                    }
                };

                // eslint-disable-next-line no-constant-condition
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });

                    let sep;
                    // SSE frames are delimited by a blank line ("\n\n").
                    while ((sep = buffer.indexOf('\n\n')) !== -1) {
                        const frame = buffer.slice(0, sep);
                        buffer = buffer.slice(sep + 2);
                        if (frame.trim()) handleFrame(frame);
                    }
                }

                // Flush any trailing frame that wasn't newline-terminated.
                if (buffer.trim()) handleFrame(buffer);
            } catch (error) {
                if (error.name === 'AbortError') return;
                console.error('Error streaming risk comparison insight:', error);
                onError && onError({ error: error.message || 'stream error' });
            }
        })();

        return () => controller.abort();
    },

    // Submit thumbs feedback for an ask-prompta answer (v3 endpoint —
    // absolute URL because axiosInstance is pinned to /api/v1)
    postChatFeedback: async ({ message_id, vote, report_id, channel, comment = null }) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.post(
                `${process.env.REACT_APP_BACKEND_URL}/rag/api/v3/feedback`,
                { message_id, vote, report_id, channel, comment },
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error submitting chat feedback:', error);
            throw error;
        }
    },

    // Rename a project.
    // PATCH /insights/projects/<project_id>/  body {name}
    //   -> 200 { id, name, created_at, waves: [{wave_count, report_id, report_name}] }
    renameProject: async (projectId, name) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.patch(
                `/insights/projects/${projectId}/`,
                { name },
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error renaming project:', projectId, error);
            throw error;
        }
    },

    // Delete a project and its waves. The REPORTS are kept — a wave is only the
    // row that puts a report in a project.
    // DELETE /insights/projects/<project_id>/[?cascade=true]
    //
    // Without `cascade`, a project that also holds documents, an Impact
    // Register, stakeholder groups or chat history is refused with 409 and
    // `data.dependents` describing what would go; ask the user, then repeat with
    // cascade: true. A project holding only waves deletes straight away.
    deleteProject: async (projectId, { cascade = false } = {}) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const url = `/insights/projects/${projectId}/${cascade ? '?cascade=true' : ''}`;
            const response = await axiosInstance.delete(url, { headers });
            return response.data;
        } catch (error) {
            console.error('Error deleting project:', projectId, error);
            throw error;
        }
    },

    // Append report(s) to a project as its next wave(s), in the order given.
    // POST /insights/projects/<project_id>/waves/  body {report_ids}
    //   -> 201 { project_id, added: [wave], waves: [wave] }
    //
    // Works on an empty project too, so this is also how a NEW project gets
    // its first waves. Needs editor/owner on the project and DIRECT access to
    // each report (403 `report_not_shareable` otherwise — see the report
    // list's `can_add_to_project`).
    addProjectWaves: async (projectId, reportIds) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.post(
                `/insights/projects/${projectId}/waves/`,
                { report_ids: reportIds },
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error adding waves to project:', projectId, error);
            throw error;
        }
    },

    // Take one report out of a project. The report itself is kept; the
    // remaining waves are renumbered 1..N by the BE.
    // DELETE /insights/projects/<project_id>/waves/<wave_id>/
    //   -> 200 { project_id, removed, report_kept, waves: [wave] }
    // Removing the last wave leaves an empty project, which stays visible to
    // its members.
    removeProjectWave: async (projectId, waveId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.delete(
                `/insights/projects/${projectId}/waves/${waveId}/`,
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error removing wave from project:', projectId, waveId, error);
            throw error;
        }
    },

    // ── MEMBERS ──────────────────────────────────────────────────────────────
    // Who can see a project. Members read EVERY report and document in it.
    // Roles: owner (everything, incl. members and delete), editor (rename,
    // add/remove waves), viewer (read only). `my_role` on each project card
    // says which the caller has.

    // GET /insights/projects/<id>/members/
    //   -> { project_id, my_role, members: [{user_id, email, first_name, last_name, role, created_at}] }
    getProjectMembers: async (projectId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(
                `/insights/projects/${projectId}/members/`,
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching project members:', projectId, error);
            throw error;
        }
    },

    // Owners only: users of the project's organization who are not members
    // yet, matching `search` (email or name). Capped at 20 by the BE.
    // GET /insights/projects/<id>/members/candidates/?search=
    //   -> [{user_id, email, first_name, last_name}]
    getProjectMemberCandidates: async (projectId, search = '') => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const queryParams = new URLSearchParams();
            if (search) queryParams.append('search', search);
            const response = await axiosInstance.get(
                `/insights/projects/${projectId}/members/candidates/${queryParams.toString() ? '?' + queryParams.toString() : ''}`,
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching member candidates:', projectId, error);
            throw error;
        }
    },

    // Owners only. POST /insights/projects/<id>/members/  body {user_id, role}
    //   -> 201 member; 400 `user_not_found` / `invalid_role`; 409 `already_member`
    addProjectMember: async (projectId, userId, role = 'viewer') => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.post(
                `/insights/projects/${projectId}/members/`,
                { user_id: userId, role },
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error adding project member:', projectId, userId, error);
            throw error;
        }
    },

    // Owners only. PATCH /insights/projects/<id>/members/<user_id>/  body {role}
    //   -> 200 member; 400 `last_owner` when demoting the only owner
    updateProjectMemberRole: async (projectId, userId, role) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.patch(
                `/insights/projects/${projectId}/members/${userId}/`,
                { role },
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error changing member role:', projectId, userId, error);
            throw error;
        }
    },

    // Owners remove anyone; any member may remove themselves (leave).
    // DELETE /insights/projects/<id>/members/<user_id>/
    //   -> 204; 400 `last_owner` when removing the only owner
    removeProjectMember: async (projectId, userId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            await axiosInstance.delete(
                `/insights/projects/${projectId}/members/${userId}/`,
                { headers }
            );
        } catch (error) {
            console.error('Error removing project member:', projectId, userId, error);
            throw error;
        }
    },

    // ── AREAS ────────────────────────────────────────────────────────────────
    // An area is a project-scoped ROW now, not a string several groups happen to
    // share. That is the whole point of these four: an area can exist with no
    // groups under it, so the workspace's tab strip is this list rather than a
    // fold over the group list, and "Add area" has something to write.

    // The tab strip, alphabetical — the BE orders it, and the FE does not
    // re-sort, so every client shows the same order.
    // Each row: { id, project, name, group_count, created_at, updated_at }.
    getAreas: async (projectId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(`/insights/projects/${projectId}/areas/`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching areas:', error);
            throw error;
        }
    },

    // Create an area. Body is { name } — `project` comes from the URL and is
    // read-only on the wire. A duplicate name in the same project is a 400 with
    // a readable message, not a 500.
    createArea: async (projectId, payload) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.post(`/insights/projects/${projectId}/areas/`, payload, { headers });
            return response.data;
        } catch (error) {
            console.error('Error creating area:', error);
            throw error;
        }
    },

    // Rename an area. The groups under it keep pointing at the same row, so a
    // rename never moves a group.
    updateArea: async (projectId, areaId, payload) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.patch(
                `/insights/projects/${projectId}/areas/${areaId}/`, payload, { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error renaming area:', error);
            throw error;
        }
    },

    // Delete an area. 204 when it is empty; 400 while it still has groups, with
    // { error, group_count, groups } naming them — NOT a cascade (that would take
    // the groups and their impacts) and not a silent detach. Callers must surface
    // the 400 rather than treat it as a generic failure: it is the answer, and it
    // says which groups to move first.
    deleteArea: async (projectId, areaId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.delete(
                `/insights/projects/${projectId}/areas/${areaId}/`, { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error deleting area:', error);
            throw error;
        }
    },

    // ── SEGMENTS ─────────────────────────────────────────────────────────────
    // A segment is a project-defined way of slicing the register: a TITLE
    // ("Region") and an ordered list of CHOICES ("EU", "US"). They replace the
    // fixed Area -> Group pair — a project can have as many segments as it
    // needs, and an impact takes at most one choice in each.
    //
    // Served by BE impacts.api.v1 (ProjectSegment*View). The workspace reaches
    // them through pages/impacts/registerApi.
    //
    //   GET    /insights/projects/<id>/segments/
    //     -> [{ id, title, position, choices: [{ id, label, position }] }]
    //   POST   /insights/projects/<id>/segments/          { title, choices: [{ label }] }
    //   PATCH  /insights/projects/<id>/segments/<sid>/    { title?, choices? }
    //     `choices` is the full ordered list: a row with an `id` is kept (and
    //     relabelled), one without is created, an id left out is deleted and its
    //     value cleared from every impact. Labels are unique per segment.
    //   DELETE /insights/projects/<id>/segments/<sid>/
    //     Clears the segment's value from every impact; never deletes an impact.
    //
    // Impacts carry `segment_values: { "<segment id>": <choice id> }`, read and
    // written as one map; a segment missing from the map is "not set".
    getSegments: async (projectId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(`/insights/projects/${projectId}/segments/`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching segments:', error);
            throw error;
        }
    },

    createSegment: async (projectId, payload) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.post(`/insights/projects/${projectId}/segments/`, payload, { headers });
            return response.data;
        } catch (error) {
            console.error('Error creating segment:', error);
            throw error;
        }
    },

    updateSegment: async (projectId, segmentId, payload) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.patch(
                `/insights/projects/${projectId}/segments/${segmentId}/`, payload, { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error updating segment:', error);
            throw error;
        }
    },

    deleteSegment: async (projectId, segmentId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.delete(
                `/insights/projects/${projectId}/segments/${segmentId}/`, { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error deleting segment:', error);
            throw error;
        }
    },

    // Get the stakeholder groups registered against a project.
    // The Registry belongs to the change programme, not to one wave: every wave
    // of the project reads and writes the same groups.
    getStakeholderGroups: async (projectId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(`/insights/projects/${projectId}/stakeholder-groups/`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching stakeholder groups:', error);
            throw error;
        }
    },

    // Create a stakeholder group under a project
    createStakeholderGroup: async (projectId, payload) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.post(`/insights/projects/${projectId}/stakeholder-groups/`, payload, { headers });
            return response.data;
        } catch (error) {
            console.error('Error creating stakeholder group:', error);
            throw error;
        }
    },

    // Update an existing stakeholder group.
    // The BE detail route is NOT insight-scoped: PATCH /insights/stakeholder-groups/<id>/
    updateStakeholderGroup: async (groupId, payload) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.patch(`/insights/stakeholder-groups/${groupId}/`, payload, { headers });
            return response.data;
        } catch (error) {
            console.error('Error updating stakeholder group:', error);
            throw error;
        }
    },

    // Delete a stakeholder group. Like update, the detail route is NOT
    // insight-scoped: DELETE /insights/stakeholder-groups/<id>/
    // The BE cascades — Impact.group is on_delete=CASCADE — so this takes the
    // group's impacts with it. Callers must say so before confirming.
    deleteStakeholderGroup: async (groupId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.delete(`/insights/stakeholder-groups/${groupId}/`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error deleting stakeholder group:', error);
            throw error;
        }
    },

    // ── Impact register (per-project change impacts) ──

    // List the impacts recorded against a project.
    // Rows carry a read-only `group_name` alongside the `group` id, and a
    // read-only `project`; `insight` is optional provenance — the wave an impact
    // was first logged from, not what scopes it.
    getImpacts: async (projectId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(`/insights/projects/${projectId}/impacts/`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching impacts:', error);
            throw error;
        }
    },

    // Create an impact under a project
    createImpact: async (projectId, payload) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.post(`/insights/projects/${projectId}/impacts/`, payload, { headers });
            return response.data;
        } catch (error) {
            console.error('Error creating impact:', error);
            throw error;
        }
    },

    // Update an existing impact.
    // Like stakeholder groups, the detail route is NOT insight-scoped:
    // PATCH /insights/impacts/<id>/
    updateImpact: async (impactId, payload) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.patch(`/insights/impacts/${impactId}/`, payload, { headers });
            return response.data;
        } catch (error) {
            console.error('Error updating impact:', error);
            throw error;
        }
    },

    // Delete an impact. DELETE /insights/impacts/<id>/
    deleteImpact: async (impactId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.delete(`/insights/impacts/${impactId}/`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error deleting impact:', error);
            throw error;
        }
    },

    // The pillar / change-action vocabulary for a project's impacts:
    //   { pillars: [{ key, label, definition, custom }],
    //     edges:   [{ key, label, definition, custom }],
    //     corners: [...] }   <- DEPRECATED, never read
    // The chips in the form are rendered from this response — the keys, labels and
    // hover definitions are the BE's, never hardcoded here.
    // `projectId` is load-bearing on BOTH lists: `pillars` is the canonical six
    // PLUS whatever pillars this project's own assessments invented, and `edges`
    // is the framework's four change actions PLUS the project's own — so the
    // vocabulary differs project to project. The insight-scoped route this used
    // to have is gone.
    getImpactTaxonomy: async (projectId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(`/insights/projects/${projectId}/impact-taxonomy/`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching impact taxonomy:', error);
            throw error;
        }
    },

    // ── CUSTOM CHANGE ACTIONS ────────────────────────────────────────────────
    // A project's OWN change actions — the rows `edges` carries with
    // `custom: true`, as editable records rather than as vocabulary.
    //
    // Two endpoints for one list, on purpose: the taxonomy serves what to RENDER
    // (framework four then the project's own, keys and labels, no ids), and these
    // serve what to EDIT (the project's own only, with the `id` a PATCH or DELETE
    // needs). Nothing here can touch a framework action — it has no row.

    // The project's own change actions: [{ id, key, label, definition, position,
    // custom: true, ... }], in `position` order.
    listCustomChangeActions: async (projectId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(
                `/insights/projects/${projectId}/change-actions/`, { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching custom change actions:', error);
            throw error;
        }
    },

    // Create one. Body is { label } and nothing else: the BE derives `key` from
    // the label and IGNORES a client-sent one, so the created row's own `key` is
    // the only thing safe to select afterwards — never a key guessed here.
    createCustomChangeAction: async (projectId, payload) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.post(
                `/insights/projects/${projectId}/change-actions/`, payload, { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error creating custom change action:', error);
            throw error;
        }
    },

    // Rename one. The `key` does NOT change, so every impact and activity already
    // filed under it stays filed — which is why rename is the safe answer to a
    // delete the BE refuses.
    updateCustomChangeAction: async (projectId, actionId, payload) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.patch(
                `/insights/projects/${projectId}/change-actions/${actionId}/`, payload, { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error renaming custom change action:', error);
            throw error;
        }
    },

    // Delete one. 204 while nothing uses it; 409 with
    // { error, impact_count, activity_count } once something does. That 409 is an
    // ANSWER — it says how many rows would lose their word — so callers must
    // surface it rather than treat it as a generic failure, the same rule the
    // area delete follows with its 400.
    deleteCustomChangeAction: async (projectId, actionId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.delete(
                `/insights/projects/${projectId}/change-actions/${actionId}/`, { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error deleting custom change action:', error);
            throw error;
        }
    },

    // ── PROJECT PILLARS ──────────────────────────────────────────────────────
    // The project's DECLARED pillar set — the canonical six, then its own rows:
    //   [{ id, key, name, label, definition, position, custom }]
    // The canonical rows have `id: null` and a blank definition. This is the set
    // a manager curates; the taxonomy is still what the impact picker RENDERS,
    // since it also folds in pillars the project's waves already use.
    listProjectPillars: async (projectId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(
                `/insights/projects/${projectId}/pillars/`, { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching project pillars:', error);
            throw error;
        }
    },

    // Add one: { name, definition? }. The answer is the pillar the name IS, which
    // is not always a new row — 201 for a new one, 200 for one the project
    // already has (any casing), 200 + the canonical entry when the name is one of
    // the six in any spelling ("Cultura"). Either way the body's `key` is what to
    // select, so callers never guess it.
    createProjectPillar: async (projectId, payload) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.post(
                `/insights/projects/${projectId}/pillars/`, payload, { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error creating project pillar:', error);
            throw error;
        }
    },

    // ── Activity tracker (the execution layer under an impact) ──
    //
    // An activity is one concrete thing a change manager RUNS against an impact —
    // a briefing, a course, a readiness check:
    //   { id, impact, title, description, status,
    //     planned_date, completed_date, evidence, source, approved,
    //     created_at, updated_at }
    // An activity is tracked by its title; it has no category.
    // `status` is outstanding | coming | done.
    //
    // Every route is scoped by BOTH project and impact: `impact` on the payload is
    // read-only, so the URL is what decides which impact a row belongs to.

    // List an impact's activities. Bare array, ordered by id.
    getActivities: async (projectId, impactId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(
                `/insights/projects/${projectId}/impacts/${impactId}/activities/`,
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching activities:', error);
            throw error;
        }
    },

    // Create an activity under an impact.
    createActivity: async (projectId, impactId, payload) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.post(
                `/insights/projects/${projectId}/impacts/${impactId}/activities/`,
                payload,
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error creating activity:', error);
            throw error;
        }
    },

    // Update one activity. Unlike impacts and groups the detail route IS scoped —
    // project and impact both stay in the path.
    updateActivity: async (projectId, impactId, activityId, payload) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.patch(
                `/insights/projects/${projectId}/impacts/${impactId}/activities/${activityId}/`,
                payload,
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error updating activity:', error);
            throw error;
        }
    },

    // Delete one activity. 204, no body.
    deleteActivity: async (projectId, impactId, activityId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.delete(
                `/insights/projects/${projectId}/impacts/${impactId}/activities/${activityId}/`,
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error deleting activity:', error);
            throw error;
        }
    },

    // Ask the AI to propose activities. POST because it WRITES: the suggestions
    // are persisted as Activity rows with `source: "ai_suggested"` and
    // `approved: false`, and the response is those rows —
    //   { activities: [...], targeted_impact_ids: [...], created_count }
    //
    // `impactId` is the whole API surface: pass one to suggest for that impact
    // (the panel's button), omit it to GAP-FILL — suggest for every impact in the
    // project that has no activities yet, skipping the ones a human has already
    // planned (the dashboard's button).
    //
    // An LLM call, so callers must show a loading state. A 200 rather than a 201
    // means it ran and produced nothing.
    suggestActivities: async (projectId, impactId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.post(
                `/insights/projects/${projectId}/activity-suggestions/`,
                impactId == null ? {} : { impact_id: impactId },
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error requesting activity suggestions:', error);
            throw error;
        }
    },

    // The project rollup the tracker dashboard renders:
    //   { totals:  { outstanding, coming, done, total },
    //     impacts: [{ impact_id, name, outstanding, coming, done, total }] }
    // EVERY impact appears, including ones with no activities yet — those all-zero
    // rows are the "what's missing?" answer the screen exists for.
    getActivityDashboard: async (projectId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(
                `/insights/projects/${projectId}/activity-dashboard/`,
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching the activity dashboard:', error);
            throw error;
        }
    },

    // ── Change leaders (per-project, own the stakeholder groups) ──

    // List the leaders defined on a project.
    // Each leader carries a read-only `group_ids` array — the stakeholder groups
    // assigned to them — so the workspace builds its leader -> groups tree from
    // this one response instead of a call per leader.
    getLeaders: async (projectId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(`/insights/projects/${projectId}/leaders/`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching leaders:', error);
            throw error;
        }
    },

    // Create a leader under a project. `name` may be "" (an anonymous leader);
    // `color` is required and must be a #RRGGBB hex string.
    createLeader: async (projectId, payload) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.post(`/insights/projects/${projectId}/leaders/`, payload, { headers });
            return response.data;
        } catch (error) {
            console.error('Error creating leader:', error);
            throw error;
        }
    },

    // Update an existing leader.
    // Like stakeholder groups and impacts, the detail route is NOT insight-scoped:
    // PATCH /insights/leaders/<id>/
    updateLeader: async (leaderId, payload) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.patch(`/insights/leaders/${leaderId}/`, payload, { headers });
            return response.data;
        } catch (error) {
            console.error('Error updating leader:', error);
            throw error;
        }
    },

    // Delete a leader. DELETE /insights/leaders/<id>/
    // Unlike a stakeholder group this does NOT cascade: the leader's groups stay,
    // they simply become unassigned. Callers must say so before confirming.
    deleteLeader: async (leaderId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.delete(`/insights/leaders/${leaderId}/`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error deleting leader:', error);
            throw error;
        }
    },

    // POST /insights/reports/<id>/compare-insight/ — the By-group compare
    // views' on-demand "Show AI insights":
    //   { mode: 'groups', scope_a, scope_b }            two scopes of this report
    //   { mode: 'waves',  scope_a, other_report_id }    one scope across two waves
    // Scope keys are the group-scores rows' `scope_key`. -> { status: 'ready',
    // cached, sides: [{ scope_key, name, kind, report_id, wave_count, n }],
    // focus_areas: [{ headline, detail, pillars, action }], ... }. Cached per
    // pair on the BE; a miss is one model call (~10-15s). 404 when a side is
    // hidden or unknown in its wave, 502 when the model's answer was unusable.
    getCompareInsight: async (reportId, body) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.post(`/insights/reports/${reportId}/compare-insight/`, body, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching compare insight:', error);
            throw error;
        }
    },

    // GET /insights/reports/<id>/group-actions/?scope_key= — the By-group
    // single view's "What to do next" for one selection (the row's scope_key):
    //   { status: 'ready' | 'no_impacts' | 'no_groups', project_id, scope_key,
    //     cached, actions: [{ index, title, description, impact_id,
    //     impact_title, group_name, pillars, added_activity_id }] }
    // Generated on first
    // view and cached on the BE; a miss is one or two model calls (~15-18s).
    getGroupActions: async (reportId, scopeKey) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(`/insights/reports/${reportId}/group-actions/`, {
                headers,
                params: { scope_key: scopeKey },
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching group actions:', error);
            throw error;
        }
    },

    // GET /insights/reports/<id>/group-impacts/?scope_key= — the By-group
    // single view's "Impacts & activities" panel: the selection's Register rows,
    // read-only and fast (no model):
    //   { status: 'ready' | 'no_impacts' | 'no_groups', project_id, scope_key,
    //     status_labels, impacts: [{ id, title, business_area, group_id,
    //     group_name, level, pillars, activities: [{ id, title, status,
    //     status_label, planned_date, completed_date }], activity_counts }],
    //     totals: { impacts, activities, by_status: { outstanding, coming, done } } }
    getGroupImpacts: async (reportId, scopeKey) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
            };
            const response = await axiosInstance.get(`/insights/reports/${reportId}/group-impacts/`, {
                headers,
                params: { scope_key: scopeKey },
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching group impacts:', error);
            throw error;
        }
    },

    // PATCH /insights/reports/<id>/group-actions/ { scope_key, index, activity_id }
    // — records that actions[index] was added, AFTER createActivity made the
    // activity under that action's impact. Creates nothing itself.
    markGroupActionAdded: async (reportId, body) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.patch(`/insights/reports/${reportId}/group-actions/`, body, { headers });
            return response.data;
        } catch (error) {
            console.error('Error marking group action added:', error);
            throw error;
        }
    },

    // POST /insights/reports/<id>/group-scores/chat/stream/ — the By-group
    // tab's in-card chat about ONE shown selection:
    //   { scope_key: 'baseline' | 'area:<id>' | 'group:<id>', question, history }
    // `history` is the prior turns as [{ role: 'user'|'assistant', content }],
    // ending on the assistant; the BE keeps the last 8 and stores nothing.
    //
    // Events: meta -> token* -> done, plus
    //   redirect { question, text }  no answer: the question needs data beyond
    //                                this selection — point at the Prompta chat
    //   replace  { text, reason }    swap the streamed answer for `text` (the
    //                                anonymity rewrite), sent just before done
    // Callbacks: onMeta / onToken(text) / onRedirect({ question, text }) /
    // onReplace({ text, reason }) / onDone() / onError({ error, status? }).
    // Returns a function that aborts the stream when called.
    streamGroupScoresChat: (reportId, body, { onRedirect, onReplace, ...handlers } = {}) =>
        streamSse(
            `${process.env.REACT_APP_BACKEND_URL}/api/v1/insights/reports/${reportId}/group-scores/chat/stream/`,
            body,
            {
                ...handlers,
                onEvent: (event, data) => {
                    if (event === 'redirect') onRedirect && onRedirect(data);
                    else if (event === 'replace') onReplace && onReplace(data);
                },
            }
        ),

    // Assign, reassign, or unassign a stakeholder group's leader.
    // PUT /insights/groups/<group_id>/leader/ with { leader_id: <id> | null }
    //   -> { group_id, leader_id | null }
    // PUT because a group has at most one leader and the call is idempotent; an
    // explicit null unassigns. A leader from another insight is a 400.
    setGroupLeader: async (groupId, leaderId) => {
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem(token_keys.access_token)}`,
                'Content-Type': 'application/json',
            };
            const response = await axiosInstance.put(
                `/insights/groups/${groupId}/leader/`,
                { leader_id: leaderId ?? null },
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error setting group leader:', error);
            throw error;
        }
    },

};

export default insightsApi;